"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.View = exports.useView = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var json_pointer_1 = tslib_1.__importDefault(require("json-pointer"));
var view_1 = require("@catalytic/view");
var context_1 = tslib_1.__importDefault(require("./context"));
var validator_1 = tslib_1.__importDefault(require("./validator"));
var type_1 = require("./type");
var nextID_1 = tslib_1.__importDefault(require("./nextID"));
var expression_1 = tslib_1.__importDefault(require("./expression"));
var nextID = nextID_1.default({ prefix: 'view' });
var tryCatch = function (fn) { return function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    try {
        return fn.apply(void 0, args);
    }
    catch (e) { }
}; };
var pointerGet = tryCatch(json_pointer_1.default.get);
var pointerSet = tryCatch(json_pointer_1.default.set);
exports.useView = function (props) {
    var defaultValue = props.defaultValue, node = props.node, onEvent = props.onEvent, onUpdate = props.onUpdate, optionValueReference = props.optionValueReference, schema = props.schema, propSetValue = props.setValue, _a = props.reference, reference = _a === void 0 ? '' : _a, propValue = props.value, propValueReference = props.valueReference;
    var id = React.useState(nextID())[0];
    var context = React.useContext(context_1.default);
    var expression = React.useContext(expression_1.default);
    var validator = React.useContext(validator_1.default);
    var getNode = React.useCallback(function () { return pointerGet(node, reference); }, [reference, node]);
    var getComponent = React.useCallback(function () { return context[getNode().role || view_1.Role.EDITOR][getNode().type]; }, [context, getNode]);
    var _b = getNode(), _c = _b.children, children = _c === void 0 ? [] : _c, items = _b.items, options = _b.options, optionValues = _b.optionValues, valueReference = _b.reference, _d = _b.schemaReference, schemaReference = _d === void 0 ? '' : _d, nodeValue = _b.value;
    var getReferenceParts = React.useCallback(function () { return json_pointer_1.default.parse(reference); }, [reference, getNode]);
    var getSchemaReferenceParts = React.useCallback(function () { return json_pointer_1.default.parse(schemaReference); }, [schemaReference, getNode]);
    var hasValueReference = React.useCallback(function () {
        return typeof propValueReference !== 'undefined' ||
            typeof valueReference !== 'undefined';
    }, [propValueReference, valueReference, getNode]);
    var getValueReferenceParts = React.useCallback(function () {
        return hasValueReference()
            ? json_pointer_1.default.parse(propValueReference || valueReference)
            : [];
    }, [propValueReference, valueReference, getNode]);
    var hasOptionValueReference = React.useCallback(function () { return typeof optionValueReference !== 'undefined'; }, [propValueReference, valueReference, getNode]);
    var getReference = React.useCallback(function () {
        var parts = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            parts[_i] = arguments[_i];
        }
        return parts.length
            ? json_pointer_1.default.compile(getReferenceParts().concat(parts))
            : reference;
    }, [getReferenceParts]);
    var getSchemaReference = React.useCallback(function () {
        var parts = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            parts[_i] = arguments[_i];
        }
        return parts.length
            ? json_pointer_1.default.compile(getSchemaReferenceParts().concat(parts))
            : schemaReference;
    }, [getSchemaReferenceParts]);
    var getValueReference = React.useCallback(function () {
        var parts = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            parts[_i] = arguments[_i];
        }
        return parts.length
            ? json_pointer_1.default.compile(getValueReferenceParts().concat(parts))
            : propValueReference || valueReference;
    }, [getValueReferenceParts]);
    var getSchema = React.useCallback(function () {
        return schema !== undefined
            ? pointerGet(schema, getSchemaReference())
            : undefined;
    }, [getNode, getSchemaReference]);
    var getDefaultValue = React.useCallback(function () { var _a; return (defaultValue !== undefined ? defaultValue : (_a = getSchema()) === null || _a === void 0 ? void 0 : _a.default); }, [defaultValue, getSchema]);
    var isControlled = React.useCallback(function () {
        return propValue !== undefined ||
            nodeValue !== undefined ||
            typeof propSetValue === 'function';
    }, [propValue, nodeValue]);
    var getPropValue = React.useCallback(function () {
        return propValue !== undefined
            ? propValue
            : nodeValue !== undefined
                ? nodeValue
                : getDefaultValue();
    }, [propValue, nodeValue]);
    var _e = React.useState(0), renderCount = _e[0], setRenderCount = _e[1];
    var getValue = React.useCallback(function () {
        return getPropValue() !== undefined &&
            hasValueReference() &&
            json_pointer_1.default.has(getPropValue(), getValueReference())
            ? pointerGet(getPropValue(), getValueReference())
            : hasOptionValueReference()
                ? getPropValue()
                : nodeValue;
    }, [getPropValue, getDefaultValue, getReference]);
    var _f = React.useState(getValue()), stateValue = _f[0], setStateValue = _f[1];
    var rerender = React.useCallback(function () { return setRenderCount(renderCount + 1); }, [
        renderCount
    ]);
    var getStateValue = React.useCallback(function () { return (isControlled() ? getPropValue() : stateValue); }, [stateValue, getPropValue]);
    var value = isControlled() ? getValue() : getStateValue();
    var getValidator = React.useCallback(function () { return validator.compile(getSchema() || { type: 'any' }); }, [getSchema, validator]);
    var getExpressionContext = React.useCallback(function () { return ({
        fields: getPropValue(),
        this: getValue()
    }); }, [getValue, getPropValue]);
    var getSchemaVisibility = React.useCallback(function () { var _a; return (_a = getSchema()) === null || _a === void 0 ? void 0 : _a.conditionalDependencies; }, [getSchema]);
    var getNodeVisibility = React.useCallback(function () { return getNode().visible; }, [getNode]);
    var getVisibility = React.useCallback(function () {
        var nodeVisibility = getNodeVisibility();
        var source = nodeVisibility === undefined
            ? getSchemaVisibility() || 'true'
            : typeof nodeVisibility === 'boolean'
                ? JSON.stringify(nodeVisibility)
                : nodeVisibility;
        try {
            return !!expression.evaluate(source, getExpressionContext());
        }
        catch (e) {
            return true;
        }
    }, [
        expression,
        getExpressionContext,
        getSchemaVisibility,
        getNodeVisibility
    ]);
    var getDispatcher = React.useCallback(function (event) {
        if (!onEvent)
            return;
        onEvent(tslib_1.__assign(tslib_1.__assign({}, event), { defaultValue: getDefaultValue, node: getNode, reference: getReference, schema: getSchema, value: value, valueReference: getValueReference, validator: getValidator, visible: getVisibility }));
    }, [
        onEvent,
        getDefaultValue,
        getNode,
        getReference,
        getSchema,
        getValueReference,
        value,
        getValidator,
        getVisibility
    ]);
    var dispatchUpdate = React.useCallback(function (event) {
        if (typeof onUpdate === 'function') {
            onUpdate(event);
        }
        getDispatcher(tslib_1.__assign({ type: type_1.NodeEventType.Update }, event));
    }, [onUpdate, getDispatcher]);
    var setValue = React.useCallback(function (nextValue) {
        dispatchUpdate({
            value: nextValue,
            valueReference: getValueReference()
        });
        if (!isControlled()) {
            setStateValue(nextValue);
            if (typeof nextValue === 'object') {
                rerender();
            }
            return;
        }
        if (typeof propSetValue === 'function') {
            if (hasValueReference()) {
                var nextPropValue = typeof getPropValue() === 'object' &&
                    getPropValue() &&
                    !Array.isArray(getPropValue())
                    ? Object.assign({}, getPropValue())
                    : getPropValue();
                pointerSet(nextPropValue, getValueReference(), nextValue);
                propSetValue(nextPropValue);
            }
            else {
                propSetValue(nextValue);
            }
        }
    }, [isControlled, getValue, propSetValue]);
    var getSetValueSetter = React.useCallback(function () { return (typeof propSetValue === 'function' ? propSetValue : setValue); }, [propSetValue, setValue]);
    var getOptionSchemaReferenceParts = React.useCallback(function () {
        return getSchemaReferenceParts().concat(json_pointer_1.default.parse((options === null || options === void 0 ? void 0 : options.schemaReference) || ''));
    }, [options, getNode]);
    var getOptionSchemaReference = React.useCallback(function () {
        var parts = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            parts[_i] = arguments[_i];
        }
        return parts.length
            ? json_pointer_1.default.compile(getOptionSchemaReferenceParts().concat(parts))
            : json_pointer_1.default.compile(getOptionSchemaReferenceParts());
    }, [getOptionSchemaReferenceParts]);
    var getOptionSchema = React.useCallback(function () {
        return schema !== undefined && (options === null || options === void 0 ? void 0 : options.schemaReference) !== undefined
            ? pointerGet(schema, getOptionSchemaReference())
            : undefined;
    }, [getNode, getOptionSchemaReference]);
    var getOptionValues = React.useCallback(function () { return getOptionSchema() || optionValues; }, [getSchema, optionValues, options]);
    var renderChildren = React.useCallback(function () {
        return children.map(function (_v, i) {
            return (React.createElement(exports.View, { key: id + "-child-" + i, reference: getReference('children', i), node: node, onEvent: onEvent, onUpdate: onUpdate, schema: schema, setValue: getSetValueSetter(), value: getStateValue(), validator: getValidator, visible: getVisibility }));
        });
    }, [
        children,
        value,
        getValueReference,
        getStateValue,
        getSetValueSetter,
        getReference,
        getValidator,
        getVisibility
    ]);
    var renderItems = React.useCallback(function () {
        return Array.isArray(value) && items
            ? value.map(function (_v, i) { return (React.createElement(exports.View, { key: id + "-item-" + i, reference: getReference('items'), node: node, onEvent: onEvent, onUpdate: onUpdate, setValue: getSetValueSetter(), value: getStateValue(), valueReference: getValueReference(i), validator: getValidator, visible: getVisibility })); })
            : null;
    }, [
        items,
        value,
        getValueReference,
        getStateValue,
        getSetValueSetter,
        getReference,
        getValidator,
        getVisibility
    ]);
    var renderOptions = React.useCallback(function () {
        return Array.isArray(getOptionValues()) && options
            ? getOptionValues().map(function (v, i) { return (React.createElement(exports.View, { key: id + "-option-" + i, reference: getReference('options'), node: node, onEvent: onEvent, onUpdate: onUpdate, optionValueReference: "/" + i, schema: schema, setValue: getSetValueSetter(), value: v, validator: getValidator, visible: getVisibility })); })
            : null;
    }, [
        options,
        getOptionValues,
        value,
        getValueReference,
        getStateValue,
        getSetValueSetter,
        getReference,
        getValidator,
        getVisibility
    ]);
    var render = function () {
        var Component = getComponent();
        if (!Component) {
            return null;
        }
        return (React.createElement(Component, { children: renderChildren, defaultValue: getDefaultValue, dispatch: getDispatcher, items: renderItems, node: getNode, reference: getReference, options: renderOptions, optionValues: getOptionValues, schema: getSchema, setValue: setValue, value: value, valueReference: getValueReference, validator: getValidator, visible: getVisibility }));
    };
    return {
        context: context,
        isControlled: isControlled,
        getComponent: getComponent,
        getDefaultValue: getDefaultValue,
        getDispatcher: getDispatcher,
        getNode: getNode,
        getReference: getReference,
        getSchema: getSchema,
        getValidator: getValidator,
        getVisibility: getVisibility,
        renderChildren: renderChildren,
        render: render,
        renderItems: renderItems,
        renderOptions: renderOptions,
        setValue: setValue,
        value: value
    };
};
exports.View = function (props) {
    var render = exports.useView(props).render;
    return render();
};
exports.View.displayName = 'View';
//# sourceMappingURL=view.js.map